import {
  type Pitch,
  type Step,
  type Alter,
  STEPS,
  midiOf,
  mod,
  stepIndex,
  fifthsOf,
  pitchClassName,
  isStep,
} from './pitch';
import { normalizeFifths } from './keysig';

export interface IntervalDef {
  idx: number;
  /** Short name: P1, m2, A4… */
  short: string;
  /** Long name: "Perfect fourth" */
  name: string;
  /** Diatonic number (1 = unison, 8 = octave). */
  num: number;
  semis: number;
}

const QUALITY: Record<string, string> = { P: 'Perfect', m: 'Minor', M: 'Major', A: 'Augmented', d: 'Diminished' };
const ORD: Record<number, string> = {
  1: 'unison',
  2: '2nd',
  3: '3rd',
  4: '4th',
  5: '5th',
  6: '6th',
  7: '7th',
  8: 'octave',
};
const ORD_LONG: Record<number, string> = { 4: 'fourth', 5: 'fifth' };

const TABLE: Array<[string, number, number]> = [
  ['P1', 1, 0],
  ['d2', 2, 0],
  ['m2', 2, 1],
  ['M2', 2, 2],
  ['A2', 2, 3],
  ['d3', 3, 2],
  ['m3', 3, 3],
  ['M3', 3, 4],
  ['A3', 3, 5],
  ['d4', 4, 4],
  ['P4', 4, 5],
  ['A4', 4, 6],
  ['d5', 5, 6],
  ['P5', 5, 7],
  ['A5', 5, 8],
  ['d6', 6, 7],
  ['m6', 6, 8],
  ['M6', 6, 9],
  ['A6', 6, 10],
  ['d7', 7, 9],
  ['m7', 7, 10],
  ['M7', 7, 11],
  ['A7', 7, 12],
  ['P8', 8, 12],
];

/** The 24 spelled intervals of the Transpose dialog, index 0…23. */
export const INTERVALS: readonly IntervalDef[] = TABLE.map(([short, num, semis], idx) => {
  const q = QUALITY[short[0]];
  const name =
    num === 1 ? 'Perfect unison' : num === 8 ? 'Perfect octave' : `${q} ${q === 'Perfect' ? ORD_LONG[num] ?? ORD[num] : ORD[num]}`;
  return { idx, short, name, num, semis };
});

export type Direction = 1 | -1;

/** Respell a pitch whose alteration is outside ±2 onto the nearest letter with |alter| ≤ 1. */
function respell(target: number, step: Step, octave: number): Pitch {
  let si = stepIndex(step) + octave * 7;
  for (let i = 0; i < 7; i++) {
    const s = STEPS[mod(si, 7)];
    const o = Math.floor(si / 7);
    const alter = target - midiOf({ step: s, alter: 0, octave: o });
    if (Math.abs(alter) <= 1) return { step: s, alter: alter as Alter, octave: o };
    si += alter > 0 ? 1 : -1;
  }
  throw new Error(`cannot respell MIDI ${target}`);
}

/**
 * Transpose a spelled pitch by a spelled interval.
 * Returns the result and whether it had to be respelled (|alter| > 2).
 */
export function transposePitchEx(p: Pitch, intervalIdx: number, dir: Direction): { pitch: Pitch; respelled: boolean } {
  const iv = INTERVALS[intervalIdx];
  if (!iv) throw new RangeError(`bad interval index ${intervalIdx}`);
  const si = stepIndex(p.step) + dir * (iv.num - 1);
  const octave = p.octave + Math.floor(si / 7);
  const step = STEPS[mod(si, 7)];
  const target = midiOf(p) + dir * iv.semis;
  const alter = target - midiOf({ step, alter: 0, octave });
  if (Math.abs(alter) > 2) return { pitch: respell(target, step, octave), respelled: true };
  return { pitch: { step, alter: alter as Alter, octave }, respelled: false };
}

export function transposePitch(p: Pitch, intervalIdx: number, dir: Direction): Pitch {
  return transposePitchEx(p, intervalIdx, dir).pitch;
}

/** Signed semitone value of an interval in a direction. */
export function intervalSemis(intervalIdx: number, dir: Direction): number {
  return dir * INTERVALS[intervalIdx].semis;
}

/** Line-of-fifths displacement of an interval (P5 = +1, P4 = −1, M2 = +2, A4 = +6, P8 = 0). */
export function intervalFifths(intervalIdx: number): number {
  const c: Pitch = { step: 'C', alter: 0, octave: 4 };
  return fifthsOf(transposePitch(c, intervalIdx, 1));
}

/** Transpose a key signature; normalised into −7…7 by preferring fewer accidentals. */
export function transposeKeyFifths(fifths: number, intervalIdx: number, dir: Direction): { fifths: number; shift: number } {
  return normalizeFifths(fifths + dir * intervalFifths(intervalIdx));
}

/** Slider value (−23…+23) ↔ (index, direction). */
export function sliderToInterval(v: number): { idx: number; dir: Direction } {
  return { idx: Math.min(23, Math.abs(v)), dir: v < 0 ? -1 : 1 };
}
export function intervalToSlider(idx: number, dir: Direction): number {
  return idx * dir;
}

/** Live preview label, e.g. "C → F (+5 semitones)" or "C → G (−5 semitones)". */
export function transposePreview(intervalIdx: number, dir: Direction): string {
  const r = transposePitch({ step: 'C', alter: 0, octave: 4 }, intervalIdx, dir);
  const s = intervalSemis(intervalIdx, dir);
  const sign = s > 0 ? '+' : s < 0 ? '−' : '±';
  const unit = Math.abs(s) === 1 ? 'semitone' : 'semitones';
  return `C → ${pitchClassName(r)} (${sign}${Math.abs(s)} ${unit})`;
}

/**
 * Transpose the letters in a chord symbol: "Bbm7/F" + P4 up → "Ebm7/Bb".
 * Root is the leading letter (+accidentals); an optional "/X" names the bass.
 */
export function transposeChordName(name: string, intervalIdx: number, dir: Direction): string {
  const re = /(^|\/)([A-G])(bb|b|♭|##|#|♯|x)?/g;
  return name.replace(re, (_m, pre: string, letter: string, acc: string | undefined) => {
    if (!isStep(letter)) return _m;
    const alter = acc === 'bb' ? -2 : acc === 'b' || acc === '♭' ? -1 : acc === '#' || acc === '♯' ? 1 : acc === '##' || acc === 'x' ? 2 : 0;
    const t = transposePitch({ step: letter, alter: alter as Alter, octave: 4 }, intervalIdx, dir);
    const out = t.step + (t.alter === -2 ? 'bb' : t.alter === -1 ? 'b' : t.alter === 1 ? '#' : t.alter === 2 ? '##' : '');
    return pre + out;
  });
}

/** Find the interval index matching a diatonic number and semitone count, if any. */
export function findInterval(num: number, semis: number): number {
  return INTERVALS.findIndex((iv) => iv.num === num && iv.semis === semis);
}
